import React, { useState } from 'react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../redux/store';
import { logout } from '../../redux/slices/userSlice';
import {
  Box,
  Button,
  Container,
  IconButton,
  InputBase,
  Link,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Typography,
} from '@mui/material';
import {
  KeyboardArrowDown,
  Business,
  Person,
  Assignment,
  ContentCopy,
  Timeline,
  Search,
  Logout,
  Home,
  Dashboard,
  BusinessCenter,
  Description,
  ShoppingCart,
  Inventory,
  People,
  AdminPanelSettings,
  Campaign,
} from '@mui/icons-material';
import styles from './MainNavbar.module.css';

const MainNavbar: React.FC = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { user } = useSelector((state: RootState) => state.user);
  const isAdmin = user?.role === 'admin';

  // Uncomment when client selection is implemented
  // const [selectedClient, setSelectedClient] = useState<string | null>(null);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [searchQuery, setSearchQuery] = useState('');

  const handleUserMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleUserMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = () => {
    dispatch(logout());
    handleUserMenuClose();
    navigate('/login');
  };

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      // Implement search functionality
      console.log('Searching for:', searchQuery);
    }
  };

  return (
    <>
      {/* Top Navigation */}
      <div className={styles.topNav}>
        <Container className={styles.container}>
          <div className={styles.topNavLinks}>
            <Link component={RouterLink} to="/" className={styles.navLink}>
              Portfólio
            </Link>
            <Link component={RouterLink} to="/home" className={styles.navLink}>
              Painel
            </Link>
            {isAdmin && (
              <Link component={RouterLink} to="/admin" className={styles.navLink}>
                Admin
              </Link>
            )}
          </div>
          <div className={styles.topNavRight}>
            {/* Client selector - temporarily disabled
            <div className={styles.clientSelector}>
              <div className={styles.clientSelectorButton}>
                <Box className={styles.iconContainer}>
                  <img src="/icons/store.svg" alt="Store" className={styles.iconSmall} />
                </Box>
                <span className={styles.selectorText}>Selecione um cliente</span>
                <img
                  src="/images/arrow-drop-down-filled.svg"
                  alt="Dropdown"
                  className={styles.iconGray}
                />
              </div>
            </div>
            */}
            <div className={styles.userMenu} onClick={handleUserMenuOpen}>
              <div className={styles.userAvatar}>
                <Person fontSize="small" />
              </div>
              <span className={styles.userName}>{user?.username || 'Usuário'}</span>
              <KeyboardArrowDown fontSize="small" className={styles.iconGray} />
            </div>
            <Menu
              anchorEl={anchorEl}
              open={Boolean(anchorEl)}
              onClose={handleUserMenuClose}
              anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'right',
              }}
              transformOrigin={{
                vertical: 'top',
                horizontal: 'right',
              }}
            >
              <MenuItem onClick={handleUserMenuClose}>Perfil</MenuItem>
              <MenuItem onClick={handleUserMenuClose}>Configurações</MenuItem>

              {isAdmin && (
                <>
                  <Divider />
                  <Typography variant="subtitle2" color="text.secondary" sx={{ px: 2, py: 1 }}>
                    Admin
                  </Typography>
                  <MenuItem component={RouterLink} to="/admin" onClick={handleUserMenuClose}>
                    <ListItemIcon>
                      <AdminPanelSettings fontSize="small" />
                    </ListItemIcon>
                    <ListItemText>Painel Admin</ListItemText>
                  </MenuItem>
                  <MenuItem
                    component={RouterLink}
                    to="/admin/opportunities"
                    onClick={handleUserMenuClose}
                  >
                    <ListItemIcon>
                      <BusinessCenter fontSize="small" />
                    </ListItemIcon>
                    <ListItemText>Todas Oportunidades</ListItemText>
                  </MenuItem>
                  <MenuItem component={RouterLink} to="/admin/quotes" onClick={handleUserMenuClose}>
                    <ListItemIcon>
                      <Description fontSize="small" />
                    </ListItemIcon>
                    <ListItemText>Todas Cotações</ListItemText>
                  </MenuItem>
                  <MenuItem component={RouterLink} to="/admin/orders" onClick={handleUserMenuClose}>
                    <ListItemIcon>
                      <ShoppingCart fontSize="small" />
                    </ListItemIcon>
                    <ListItemText>Todos Pedidos</ListItemText>
                  </MenuItem>
                  <MenuItem
                    component={RouterLink}
                    to="/admin/products"
                    onClick={handleUserMenuClose}
                  >
                    <ListItemIcon>
                      <Inventory fontSize="small" />
                    </ListItemIcon>
                    <ListItemText>Todos Produtos</ListItemText>
                  </MenuItem>
                  <MenuItem
                    component={RouterLink}
                    to="/admin/templates"
                    onClick={handleUserMenuClose}
                  >
                    <ListItemIcon>
                      <ContentCopy fontSize="small" />
                    </ListItemIcon>
                    <ListItemText>Templates</ListItemText>
                  </MenuItem>
                  <MenuItem
                    component={RouterLink}
                    to="/admin/campaigns"
                    onClick={handleUserMenuClose}
                  >
                    <ListItemIcon>
                      <Campaign fontSize="small" />
                    </ListItemIcon>
                    <ListItemText>Campanhas</ListItemText>
                  </MenuItem>
                  <MenuItem component={RouterLink} to="/admin/users" onClick={handleUserMenuClose}>
                    <ListItemIcon>
                      <People fontSize="small" />
                    </ListItemIcon>
                    <ListItemText>Todos Usuários</ListItemText>
                  </MenuItem>
                  <Divider />
                </>
              )}

              <MenuItem onClick={handleLogout}>
                <Logout fontSize="small" sx={{ mr: 1 }} />
                Sair
              </MenuItem>
            </Menu>
          </div>
        </Container>
      </div>

      {/* Logo and Main Navigation */}
      <div className={styles.mainNav}>
        <Container maxWidth="lg" className={styles.container}>
          <div
            className={styles.mainNavContent}
            style={{ justifyContent: 'space-between', width: '100%' }}
          >
            <div className={styles.logoContainer}>
              <img
                src="/images/acer-for-business-logo2.png"
                alt="Acer for Business"
                className={styles.logo}
              />
            </div>
            <div className={styles.mainNavButtons} style={{ alignSelf: 'flex-end' }}>
              {!isAdmin && (
                <>
                  <NavButton
                    icon="opportunity"
                    text="Registrar Oportunidade"
                    to="/opportunities/new"
                  />
                  <NavButton icon="quote" text="Cotação" to="/quotes" />
                  <NavButton icon="clients" text="Painel de Clientes" to="/customers" />
                  <NavButton icon="campaigns" text="Campanhas" to="/campaigns" />
                  <NavButton icon="orders" text="Meus Pedidos" to="/orders" />
                  <Button
                    component={RouterLink}
                    to="/"
                    variant="contained"
                    className={styles.storeButton}
                  >
                    Loja do Revendedor
                  </Button>
                </>
              )}
              {isAdmin && (
                <>
                  <NavButton icon="admin" text="Painel" to="/admin" />
                  <NavButton icon="opportunity" text="Oportunidades" to="/admin/opportunities" />
                  <NavButton icon="quote" text="Cotações" to="/admin/quotes" />
                  <NavButton icon="orders" text="Pedidos" to="/admin/orders" />
                  <NavButton icon="products" text="Produtos" to="/admin/products" />
                  <NavButton icon="templates" text="Templates" to="/admin/templates" />
                  <NavButton icon="campaigns" text="Campanhas" to="/admin/campaigns" />
                  <NavButton icon="users" text="Usuários" to="/admin/users" />
                </>
              )}
            </div>
          </div>
        </Container>
      </div>
    </>
  );
};

interface NavButtonProps {
  icon: string;
  text: string;
  to: string;
}

function NavButton({ icon, text, to }: NavButtonProps) {
  let iconPath;

  switch (icon) {
    case 'opportunity':
      iconPath = '/icons/opportunity.svg';
      break;
    case 'quote':
      iconPath = '/icons/quote.svg';
      break;
    case 'clients':
      iconPath = '/icons/painel-de-clientes.svg';
      break;
    case 'orders':
      iconPath = '/icons/meus-pedidos.svg';
      break;
    case 'products':
      iconPath = '/icons/store.svg';
      break;
    case 'admin':
      iconPath = '/icons/store.svg'; // Use an appropriate icon
      break;
    case 'users':
      iconPath = '/icons/users.svg';
      break;
    case 'templates':
      iconPath = '/icons/quote.svg';
      break;
    case 'campaigns':
      iconPath = '/icons/quote.svg';
      break;
    default:
      iconPath = null;
  }

  return (
    <Link component={RouterLink} to={to} className={styles.navButton}>
      <div className={styles.navButtonIconContainer}>
        {iconPath && <img src={iconPath} alt={text} className={styles.navButtonIcon} />}
      </div>
      <span className={styles.navButtonText}>{text}</span>
    </Link>
  );
}

export default MainNavbar;
